import {
  ANALYTICS_DIMENSIONS,
  type AnalyticsDimension,
  analyticsDimensionValueLabel,
} from "@loot-os/api-contract";

import {
  BREAKDOWN_SERIES_MAX,
  type InsightChartRow,
  type InsightMeasure,
} from "./analyticsInsights";

/**
 * One event broken down by one dimension: the series are the dimension's values. The rule the
 * rest of Insights follows decides what may be drawn here too. Events under different values
 * are different events, so they add up: past the palette's five colors the smaller values are
 * charted together as "Other", and the table has a total. Shoppers do not add up, because one
 * shopper can be under two values in a bucket (a phone and a laptop), so for shoppers there is
 * no "Other" and no total: the top values are charted alone and the table lists the rest.
 */

/** A breakdown rollup as `analytics.breakdown` answers it. */
export interface BreakdownRow {
  bucket: string;
  value: string;
  events: number;
  users: number;
}

export const BREAKDOWN_NONE = "none";

/** The builder's choices: no breakdown, then each dimension in registry order. */
export const BREAKDOWN_OPTIONS: readonly { value: string; label: string }[] = [
  { value: BREAKDOWN_NONE, label: "None" },
  ...Object.entries(ANALYTICS_DIMENSIONS).map(([value, spec]) => ({ value, label: spec.label })),
];

export function breakdownLabel(dimension: AnalyticsDimension): string {
  return ANALYTICS_DIMENSIONS[dimension].label;
}

/**
 * The values seen over the window, by their events descending and then by value. Ranked by
 * events whatever the measure, so switching to shoppers keeps each value's series and color.
 */
export function rankBreakdownValues(rows: readonly BreakdownRow[]): string[] {
  const events = new Map<string, number>();
  for (const row of rows) events.set(row.value, (events.get(row.value) ?? 0) + row.events);
  return [...events.entries()]
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .map(([value]) => value);
}

/** One charted series of a breakdown: a single value, or (events only) the rest as "Other". */
export interface BreakdownSeries {
  /** Its key in a chart row, by position, for the reason `InsightSeries.key` gives. */
  key: string;
  label: string;
  color: string;
  /** The values it draws: one, or for "Other" every value past the charted ones. */
  values: readonly string[];
  isOther: boolean;
}

/**
 * The series for a breakdown, at most `BREAKDOWN_SERIES_MAX`. Events past the cap fold into
 * "Other", which is a sum of events and therefore exact; shoppers past the cap are left off the
 * chart (`uncharted` says how many) because a sum of shoppers is not a count of shoppers.
 */
export function breakdownSeries(args: {
  rows: readonly BreakdownRow[];
  dimension: AnalyticsDimension;
  measure: InsightMeasure;
}): { series: BreakdownSeries[]; uncharted: number } {
  const ranked = rankBreakdownValues(args.rows);
  const overflow = ranked.length > BREAKDOWN_SERIES_MAX;
  const foldsOther = overflow && args.measure === "events";
  const shown = ranked.slice(0, foldsOther ? BREAKDOWN_SERIES_MAX - 1 : BREAKDOWN_SERIES_MAX);
  const series: BreakdownSeries[] = shown.map((value, i) => ({
    key: `s${i}`,
    label: analyticsDimensionValueLabel(args.dimension, value),
    color: `var(--chart-${i + 1})`,
    values: [value],
    isOther: false,
  }));
  if (foldsOther) {
    series.push({
      key: `s${shown.length}`,
      label: "Other",
      color: `var(--chart-${shown.length + 1})`,
      values: ranked.slice(shown.length),
      isOther: true,
    });
  }
  return { series, uncharted: overflow && !foldsOther ? ranked.length - shown.length : 0 };
}

/**
 * The chart's rows: every bucket of the window with each series' value or zero. A series of one
 * value reads that value's row; "Other" adds its values' events, and exists for events alone.
 */
export function pivotBreakdown(args: {
  rows: readonly BreakdownRow[];
  series: readonly BreakdownSeries[];
  measure: InsightMeasure;
  buckets: readonly Date[];
}): InsightChartRow[] {
  const cell = new Map(args.rows.map((row) => [`${row.bucket}|${row.value}`, row[args.measure]]));
  return args.buckets.map((bucket) => {
    const iso = bucket.toISOString();
    const chartRow: InsightChartRow = { bucket: iso };
    for (const s of args.series) {
      chartRow[s.key] = s.values.reduce(
        (sum, value) => sum + (cell.get(`${iso}|${value}`) ?? 0),
        0,
      );
    }
    return chartRow;
  });
}

interface BreakdownTableRowBase {
  value: string;
  label: string;
  /** The color of the series that draws this value alone; null when it is uncharted or in "Other". */
  color: string | null;
  avgPerBucket: number;
  peakPerBucket: number;
}

/** One value over the window. Events have a total; shoppers do not, as on the rest of the page. */
export type BreakdownTableRow =
  | (BreakdownTableRowBase & { measure: "events"; total: number })
  | (BreakdownTableRowBase & { measure: "users" });

/** The table under the chart: every value seen, charted or not, in the chart's order. */
export function breakdownTableRows(args: {
  rows: readonly BreakdownRow[];
  series: readonly BreakdownSeries[];
  dimension: AnalyticsDimension;
  measure: InsightMeasure;
}): BreakdownTableRow[] {
  const colorOf = new Map(
    args.series.flatMap((s) =>
      s.isOther ? [] : s.values.map((value) => [value, s.color] as const),
    ),
  );
  return rankBreakdownValues(args.rows).map((value) => {
    const values = args.rows.filter((row) => row.value === value).map((row) => row[args.measure]);
    const sum = values.reduce((total, n) => total + n, 0);
    const base = {
      value,
      label: analyticsDimensionValueLabel(args.dimension, value),
      color: colorOf.get(value) ?? null,
      avgPerBucket: values.length > 0 ? Math.round(sum / values.length) : 0,
      peakPerBucket: Math.max(0, ...values),
    };
    return args.measure === "events"
      ? { ...base, measure: "events", total: sum }
      : { ...base, measure: "users" };
  });
}

/**
 * What "Unknown" (and for segments "Anonymous") means under a dimension, said under the chart
 * so a large Unknown share reads as what it is rather than as a fault.
 */
export function breakdownHint(dimension: AnalyticsDimension): string {
  return dimension === "segment"
    ? "A customer segment is what the shopper spent over the 30 days before the event, as of their last session. Anonymous is an event no signed-in shopper sent; Unknown is an event recorded before breakdowns existed."
    : "Unknown is an event recorded without this fact: before breakdowns existed, by a request no browser made, or a server event (an open, a claim, an order) whose shopper had not allowed analytics. Server events carry the browser's operating system, device and country only while the shopper's analytics consent is open.";
}
